import React from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon } from "lucide-react";
import { series, type SeriesId } from "../data/series";
import { futureLines } from "../data/productLines";
type HomeProps = {
  launchSeries: SeriesId;
};
const paths = [{
  to: "/wholesale",
  title: "Stock it in your shop",
  text: "Wholesale pricing, countertop displays, and account applications."
}];
export function Home({
  launchSeries
}: HomeProps) {
  const launch = series.find(s => s.id === launchSeries) ?? series[0];
  const next = series.filter(s => s.id !== launch.id);
  return <>
      <section className="mx-auto max-w-6xl px-5 pb-16 pt-10 md:pt-14" data-id="element-127" data-mp="29flofsz8n8to">
        <h1 className="font-display text-6xl font-bold uppercase leading-[0.95] tracking-tight text-ink md:text-8xl" data-id="element-128" data-mp="2dhqi2rpos5gb">
          Sklunx Krushers
        </h1>
        <div className="mt-10 grid items-start gap-10 md:grid-cols-[1fr_1.4fr]" data-id="element-129" data-mp="3kuqvtaabdvjm">
          <div className="flex flex-col gap-6 md:pt-4" data-id="element-130" data-mp="3ovubnwsd3rku">
            <p className="text-sm font-semibold uppercase text-muted" data-id="element-131" data-mp="30wrpihk53zg2">Series 01 · {launch.name}</p>
            <p className="text-lg text-ink" data-id="element-132" data-mp="2vxow5udivhnx">
              Collectible character pill crushers. Each one is a 3-in-1 crusher, splitter, and storage cup with a face. We release one limited series of three characters at a time.
            </p>
            <p className="text-muted" data-id="element-133" data-mp="2mvyu7um0l0bw">A new brand from SKLUNX.com, alongside No Problemo, Pound Town, and $5 Bumps.</p>
            <div className="flex flex-wrap gap-3" data-id="element-134" data-mp="2hzi3qtrce9vn">
              <Link to="/wholesale" className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 font-medium text-paper transition-colors duration-150 hover:bg-ink/85" data-id="element-135" data-mp="2d7gw5l54d7by">
                Wholesale pricing <ArrowRightIcon className="h-4 w-4" data-id="element-136" data-mp="239awzbap6fas" />
              </Link>
              <a href="https://sklunx.com" className="inline-flex items-center rounded-full border border-ink px-6 py-3 font-medium text-ink transition-colors duration-150 hover:bg-mist" data-id="element-137" data-mp="2376hrx7cltv7">
                Shop SKLUNX
              </a>
            </div>
          </div>
          <img src={launch.image} alt={`${launch.name}: ${launch.characters.join(", ")} pill crushers`} className="w-full rounded-2xl" data-id="element-138" data-mp="1kjxl4rsiwd2o" />
        </div>
      </section>

      <section className="border-y border-line bg-mist/60" data-id="element-139" data-mp="1an2oyfjq2ork">
        <div className="mx-auto max-w-6xl px-5 py-16" data-id="element-140" data-mp="1aonj4424k5ow">
          <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-141" data-mp="1fmp2kq3gq70w">
            <h2 className="font-display text-4xl font-bold uppercase tracking-tight text-ink" data-id="element-142" data-mp="23aouhtcig3h2">Upcoming character series</h2>
            <p className="max-w-sm text-sm text-muted" data-id="element-143" data-mp="2899vhuzl6r6k">Each series is funded by sales of the one before it.</p>
          </div>
          <ul className="mt-10 grid gap-10 md:grid-cols-2" data-id="element-144" data-mp="1tl5bmnoss9m7">
            {next.map((s, i) => <li key={s.id} className="flex flex-col gap-4" data-id="element-145" data-mp="1yinmijmdkywu" data-mpi={i}>
                <img src={s.image} alt={`${s.name} pill crushers`} className="aspect-[4/5] w-full rounded-2xl object-cover" data-id="element-146" data-mp="2m9sygnp7371v" />
                <div data-id="element-147" data-mp="2r2ds9p83pk5e">
                  <p className="text-sm text-muted" data-id="element-148" data-mp="2r2vxhbcqr8pg">Series 0{i + 2}</p>
                  <h3 className="font-display text-2xl font-semibold uppercase text-ink" data-id="element-149" data-mp="fkiwiyvnwool">{s.name}</h3>
                  <p className="mt-1 text-muted" data-id="element-150" data-mp="am7omtoxgo90">{s.vibe}</p>
                </div>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-ink text-paper" data-id="element-151" data-mp="kc0o4wbspcww">
        <div className="mx-auto max-w-6xl px-5 py-16" data-id="element-152" data-mp="kd29btmizfsc">
          <h2 className="font-display text-4xl font-bold uppercase tracking-tight" data-id="element-153" data-mp="3sru67flrywg2">Future product lines</h2>
          <p className="mt-3 max-w-xl text-paper/70" data-id="element-154" data-mp="3nu11ys418cif">These come after the character series are established. Each line reuses the same shops, distributors, and SKLUNX audience.</p>
          <div className="mt-12 flex flex-col gap-16" data-id="element-155" data-mp="ot7erv6jvfn">
            {futureLines.map(line => <article key={line.id} className="grid gap-8 md:grid-cols-[1fr_1.6fr] md:items-center" data-id="element-156" data-mp="1h2jg6bx8v7vm">
                <div data-id="element-157" data-mp="1c3ymkvchzmg5">
                  <p className="text-sm font-semibold uppercase text-amber" data-id="element-158" data-mp="1c2ooi65nae9i">{line.stage}</p>
                  <h3 className="mt-1 font-display text-3xl font-semibold uppercase" data-id="element-159" data-mp="27eoic4wu3kqs">{line.name}</h3>
                  <p className="mt-3 text-paper/80" data-id="element-160" data-mp="2cc6j380p6tnp">{line.text}</p>
                  <p className="mt-4 text-sm text-paper/50" data-id="element-161" data-mp="2hc2n379eal39">{line.note}</p>
                </div>
                <div className={`grid gap-4 ${line.images.length > 1 ? "sm:grid-cols-2" : ""}`} data-id="element-162" data-mp="2mb5qyodn2l9n">
                  {line.images.map((src, i) => <img key={src} src={src} alt={`${line.name} concept ${i + 1}`} className={`w-full rounded-xl ${line.images.length === 1 ? "mx-auto max-h-[460px] w-auto" : ""}`} data-id="element-163" data-mp="2qbzevhr3002j" data-mpi={i} />)}
                </div>
              </article>)}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-16" data-id="element-164" data-mp="3a2b7l5relhl0">
        <ul className="divide-y divide-line border-y border-line" data-id="element-165" data-mp="3e1tsi1j85snu">
          {paths.map(p => <li key={p.to} data-id="element-166" data-mp="12fhvb7qnbf8l">
              <Link to={p.to} className="group flex items-center justify-between gap-6 py-6" data-id="element-167" data-mp="12frjqqw1lec6">
                <div data-id="element-168" data-mp="ycli5wjvkbc1">
                  <h3 className="font-display text-2xl font-semibold uppercase text-ink" data-id="element-169" data-mp="yc3cyaf8imrz">{p.title}</h3>
                  <p className="text-muted" data-id="element-170" data-mp="tge93uw6mjpv">{p.text}</p>
                </div>
                <ArrowRightIcon className="h-5 w-5 shrink-0 text-ink transition-transform duration-150 group-hover:translate-x-1" data-id="element-171" data-mp="ogidok1jw0lx" />
              </Link>
            </li>)}
        </ul>
      </section>
    </>;
}